import { useTranslation } from 'react-i18next';
import { useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import { Box, Button, Flex, Text } from '@/components/common';
import { ProfilePhoto } from '@/components/profile/ProfilePhoto';
import type { ProfilePhoto as ProfilePhotoModel } from '@/types/models';
import { normalizePhotos } from '@/utils';

interface ProfilePhotoCarouselProps {
  photos: unknown;
  name: string;
  renderOverlay?: (photo: ProfilePhotoModel) => ReactNode;
}

const SWIPE_THRESHOLD_PX = 40;

export function ProfilePhotoCarousel({ photos, name, renderOverlay }: ProfilePhotoCarouselProps) {
  const { t } = useTranslation('profile');
  const [activeIndex, setActiveIndex] = useState(0);
  const startXRef = useRef<number | null>(null);
  const list = normalizePhotos(photos);
  const hasPhotos = list.length > 0;
  const multi = list.length > 1;
  const safeIndex = hasPhotos ? Math.min(activeIndex, list.length - 1) : 0;

  const goTo = (index: number) => {
    if (!hasPhotos) return;
    setActiveIndex(((index % list.length) + list.length) % list.length);
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!multi) return;
    startXRef.current = event.clientX;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!multi || startXRef.current == null) return;
    const delta = event.clientX - startXRef.current;
    startXRef.current = null;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
    goTo(delta < 0 ? safeIndex + 1 : safeIndex - 1);
  };

  const onPointerCancel = () => {
    startXRef.current = null;
  };

  if (!hasPhotos) {
    return (
      <Box
        width="100%"
        flexShrink="0"
        overflow="hidden"
        style={{
          aspectRatio: 'var(--picture-aspect-ratio)',
          backgroundColor: 'var(--color-surface-darker)',
        }}
      >
        <Flex align="center" justify="center" width="100%" height="100%">
          <Text size="1" color="gray">
            {t('view.noPhotos')}
          </Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box
      width="100%"
      position="relative"
      flexShrink="0"
      overflow="hidden"
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      style={{
        touchAction: 'pan-y',
        userSelect: 'none',
      }}
    >
      <ProfilePhoto
        src={list[safeIndex].url}
        name={name}
        index={safeIndex + 1}
        overlay={renderOverlay?.(list[safeIndex])}
      />

      {multi && (
        <Flex
          position="absolute"
          align="center"
          bottom="5"
          left="50%"
          gap="1"
          px="2"
          py="1"
          style={{
            transform: 'translateX(-50%)',
            zIndex: 10,
            backgroundColor: 'var(--color-overlay)',
            borderRadius: 'var(--radius-6)',
          }}
        >
          {list.map((photo, idx) => (
            <Button
              key={photo.id}
              type="button"
              aria-label={t('view.goToPhoto', { index: idx + 1 })}
              aria-current={idx === safeIndex}
              className="transition-all"
              color={idx === safeIndex ? undefined : 'gray'}
              style={{
                height: 'var(--space-2)',
                borderRadius: 'var(--radius-6)',
                cursor: 'pointer',
                border: 'none',
                padding: 0,
                width: idx === safeIndex ? 'var(--space-3)' : 'var(--space-2)',
              }}
              onClick={event => {
                event.stopPropagation();
                goTo(idx);
              }}
              onPointerDown={event => event.stopPropagation()}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
}
